import type { ProductSignings as ProductSigningsSchema } from '../../../amplienceTypes/schemas/imported/product-signings-schema';
import type { Signing } from '../../../utilities/graphql/codegen';
import type { FC } from 'react';

import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { TextSigning } from './TextSigning';
import { useGalleryContext } from '../../../utilities/context/dynamic/GalleryContext';
import { usePDPContext } from '../../../utilities/context/static/PDPContext';
import { media } from '~/components/shared/core/styles';
import { FeatureFlag } from '~/components/shared/FeatureFlag';
import { SIGNING_COMING_SOON } from '~/utilities/constants/signings';
import { usePDPDynamicContext } from '~/utilities/context/dynamic/PDPDynamicContext';
import { useMembersOnlySignings } from '~/utilities/hooks/useMembersOnlySignings';

const FiberRankingSigning = dynamic(
  async () =>
    (await import('../productDetail/productDetailControl/FiberRankingSigning')).FiberRankingSigning,
  { ssr: false }
);

const S = {
  SigningContainer: styled.div`
    ${({ theme }) => css`
      position: absolute;
      display: flex;
      flex-direction: column;
      width: fit-content;
      height: fit-content;
      gap: 8px;
      left: 8px;
      bottom: 8px;
      z-index: 501;

      &[data-gallery-zoom-in='true'] {
        z-index: 1;
      }

      @media ${media(theme).greaterThan('lg')} {
        inset-block-end: 0;
        inset-inline-start: 0;
        gap: 4px;

        .social-proof {
          display: contents;
          height: 16px;

          &__msg {
            width: max-content;
            padding: 0 4px;
            white-space: nowrap;
            border-radius: ${theme.borders.radius.xs};
            font-weight: ${theme.variables.font.weight.medium};
            line-height: 16px;
            font-size: ${theme.font.size[2]};
            color: ${theme.colors.content.component.dark};
            background-color: ${theme.colors.background.component.glass};
          }
        }
      }

      @media (prefers-reduced-motion) {
        transition: none;
      }

      &.wideImageGallery {
        @media ${media(theme).greaterThan('lg')} {
          display: none;
          right: calc(420px + 18px);
          top: 0;
        }
      }
    `}
  `,
};

export const Signings: FC<ProductSigningsSchema> = ({ limit = 0 }) => {
  const {
    dynamicProductData: { purchasable, personalisedPrice: dynamicPersonalisedPrice },
  } = usePDPDynamicContext();
  const {
    product: { signings: productSignings, personalisedPrice: staticPersonalisedPrice },
  } = usePDPContext();
  const { galleryZoomIn } = useGalleryContext();

  const personalisedPrice = dynamicPersonalisedPrice ?? staticPersonalisedPrice;
  const { signings } = useMembersOnlySignings({
    signings: productSignings as Signing[],
    personalisedPrice,
  });

  const filteredSignings = useMemo(
    () =>
      signings?.filter((signing, index) => {
        if (!signing || index >= limit) {
          return false;
        }

        if (signing.code === SIGNING_COMING_SOON && purchasable) {
          return false;
        }

        return Boolean(signing?.showOnPdp);
      }),
    [limit, purchasable, signings]
  );

  return (
    <>
      <FeatureFlag flag="fiberRanking">
        <FeatureFlag flag="displayFiberRankingBadgeOnPDP">
          <FiberRankingSigning galleryZoomIn={galleryZoomIn} />
        </FeatureFlag>
      </FeatureFlag>

      {filteredSignings && (
        <S.SigningContainer
          data-cs-capture=""
          data-testid="signing-container"
          data-gallery-zoom-in={galleryZoomIn}
        >
          {filteredSignings.map(signing => (
            <TextSigning key={signing.label} signing={signing} />
          ))}
        </S.SigningContainer>
      )}
    </>
  );
};
